import { css, html } from 'lit'
import { customElement, property, queryAssignedElements } from 'lit/decorators.js'
import { useEffect } from 'haunted'
import { isNullishOrWhitespace } from '../functions/nullish';
import { LitHauntedElement } from '../mixins/lit-haunted-element';

const Z_INDEX_MAX_VALUE = 2147483647;

export type EPVisualAssociationScope = 'root' | 'global'

@customElement('ep-visual-association')
export class EPVisualAssociation extends LitHauntedElement {
  @property({ attribute: 'target', reflect: true })
  target?: string = '';

  @property({ attribute: 'scope', reflect: true })
  scope: EPVisualAssociationScope = 'root'

  @queryAssignedElements()
  slotted!: HTMLElement[];

  @property({ type: Boolean, attribute: 'open', reflect: true })
  isMouseOver = false;

  @property({ type: Number, attribute: 'cooldown' })
  castTime: number = 500;
  castTimeoutID?: number;

  runningAnimations = new Set<Animation>();

  render() {   
    useEffect(() => {
      if (this.isMouseOver) {
        this.castTimeoutID = window.setTimeout(() => {
          // If we're still holding the mouse over it after the delay, actually do the animations
          if (this.isMouseOver) {
            for(let target of this.acquireTarget()) {
              const animations = this.startSpotlight(target);
              animations.forEach(anim => this.runningAnimations.add(anim));
              //this.runningAnimations.add(animations);
            }
          }
        }, this.castTime);
      }
      else {
        clearTimeout(this.castTimeoutID);
        for(let animation of this.runningAnimations) {
          animation.cancel();
          this.runningAnimations.delete(animation);
        }
      }
    }, [this.isMouseOver]);
    
    return html`
      <slot @mouseover=${this._handleMouseOver} @mouseout=${this._handleMouseOut}>
      </slot>
      <div class="spotlight-backdrop" data-mouseover="${this.isMouseOver}" style="--transition-activate-delay: ${this.castTime}ms;"></div>
    `
  }

  public acquireRootNode(): Document | ShadowRoot | null {
    if (this.scope === 'root') {
      const node = this.getRootNode();
      if (node instanceof Document || node instanceof ShadowRoot) {
        return node;
      }
    }
    else if (this.scope === 'global') {
      return document;
    }

    return null;
  }

  public acquireTarget(): HTMLElement[] {
    // Acquire root node
    const root = this.acquireRootNode();

    // Return early if we had trouble getting the root node
    if (root === null) return [];
    if (isNullishOrWhitespace(this.target)) return [];

    return Array.from(root.querySelectorAll(this.target));
  }

  private startSpotlight(target: HTMLElement): Animation[] {
    const layerChange = target.animate([
      {
        //transform: 'scale(1.15)',
        //filter: 'invert(1)',
        position: 'relative',
        zIndex: Z_INDEX_MAX_VALUE - 1,
        //boxShadow: SHADOW_64,
        boxShadow: '0 0 25px 25px rgba(255,255,255,0.45)',
        //opacity: 0.0,
      },
      {
        //transform: 'scale(1.15)',
        //filter: 'invert(1)',
        position: 'relative',
        zIndex: Z_INDEX_MAX_VALUE - 1,
        //boxShadow: SHADOW_64,
        boxShadow: '0 0 25px 25px rgba(255,255,255,0.45)',
        //opacity: 1.0,
      },
    ], {
      duration: 1,
      iterations: Infinity,
    });
    const scale = target.animate([
      {
        transform: 'scale(1.0)',
      },
      {
        transform: 'scale(1.1)',
      },
    ], {
      duration: 2000,
      direction: 'alternate',
      iterations: Infinity,
      delay: 2000 * 3,
    });
    const flash = target.animate([
      {
        filter: 'brightness(0.75)',
      },
      {
        filter: 'brightness(1.25)',
      },
    ], {
      duration: 2000,
      direction: 'alternate',
      iterations: Infinity,
      delay: 2000 * 5,
    });
    return [layerChange, scale, flash];
  }

  private _handleMouseOver() {
    console.debug(`[${this.tagName}] Mouse over!`);
    this.isMouseOver = true;
  }

  private _handleMouseOut() {
    console.debug(`[${this.tagName}] Mouse out!`);
    this.isMouseOver = false;
  }

  static styles = css`
    :host {
      display: contents;
    }

    .spotlight-backdrop {
      position: fixed;
      z-index: ${Z_INDEX_MAX_VALUE - 2};
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      margin: 0;
      padding: 0;
      background: rgba(0, 0, 0, 0.3);
      pointer-events: none;
      opacity: 0;
      transition: opacity 500ms;
    }

    @media (prefers-reduced-motion) {
      .spotlight-backdrop {
        transition: none;
      }
    }
    
    .spotlight-backdrop[data-mouseover=true] {
      opacity: 1;
      transition-delay: var(--transition-activate-delay);
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-visual-association': EPVisualAssociation
  }
}
